/**
 * Utility functions for working with card behaviours and actions
 */

import type {
  UIBlock,
  BehaviourAgentExperienceAction,
} from '@/types/ui-blocks';
import type { AgentExperienceAction } from '@strivelabs/ui-blocks';

/**
 * Wraps an agent_experience action into a UIBlock with an auto-triggered behaviour.
 * This allows the action to be passed through navigation state and rendered
 * automatically by BehaviourSlot when the chat loads.
 *
 * @param action - The agent experience action to wrap
 * @returns A UIBlock with the action configured as an auto-triggered behaviour
 *
 * @example
 * ```typescript
 * const action = agentExperienceBehaviour.action;
 * const blocks = [wrapActionAsAutoTriggeredBlock(action)];
 * navigate('/chat', { state: { pendingBlocks: blocks } });
 * ```
 */
export function wrapActionAsAutoTriggeredBlock(
  action: AgentExperienceAction
): UIBlock {
  return {
    type: 'text',
    version: 'v1',
    content: '',
    behaviours: [
      {
        id: crypto.randomUUID(),
        trigger: 'auto',
        action: {
          id: action.id,
          type: 'agent_experience',
          agent_short_name: action.agent_short_name,
          experience: action.experience,
          data: action.data,
        } as BehaviourAgentExperienceAction,
      },
    ],
  };
}
